import React, { useMemo, useState } from "react";
import "./style.scss";
import Button from "components/BaseWidgets/button";
import { CloseIcon } from "assets/icons";
import Tabs from "components/BaseWidgets/tab";
import SavedTab from "../SavedTab";
import AdvancedTab from "../AdvancedTab";
import KeywordTab from "../KeywordTab";
import useBodyScroll from "hooks/useBodyScroll";
import { useSavedSearches } from "hooks/api/useSearch";
import {
  useCourses,
  useIncomes,
  useProfession,
  useSelectOptions,
  useUserCommunities,
} from "hooks/api/useListing";
import { mapToSelectOptions } from "utils/formatHelper";
import { selectOptionsMap } from "../FormSearches/helper";
import { retrieveToken } from "utils/storage";
import { getAgeOptions } from "components/Onboard/components/helper";
import { getFilterData } from "components/Explore/helper";

function SearchPage({ setOpen, open }) {
  useBodyScroll(open);

  const [activeTab, setActiveTab] = useState("1");

  const tabToSearchMode = {
    2: 1, // Advanced
    3: 2, // Saved
  };

  const search_mode = tabToSearchMode[activeTab];

  const { data, isLoading } = useSavedSearches({
    page: 1,
    limit: 8,
    search_mode: search_mode,
  });

  const savedSearches = data?.data?.saved_searches || [];

  const allQualificationIds = useMemo(() => {
    const ids = new Set();
    savedSearches?.forEach((item) => {
      let filterData = item?.filter_data;
      if (typeof filterData === "string") {
        try {
          filterData = JSON.parse(filterData);
        } catch {
          filterData = {};
        }
      }
      const q = filterData?.qualification;
      if (Array.isArray(q)) q.forEach((id) => ids.add(id));
      else if (q) ids.add(q);
    });
    return Array.from(ids);
  }, [savedSearches]);

  // listing api calls
  const { data: communityData } = useUserCommunities({ search: "" });
  const { data: incomeData } = useIncomes({ search: "" });
  const { data: courseData } = useCourses({
    search: "",
    parent_id: allQualificationIds,
  });
  const { data: professionData } = useProfession({ search: "" });
  const {
    nationality,
    maritalStatus,
    familyStatus,
    bodyType,
    qualification,
    familyType,
    complexion,
    diet,
    motherTongue,
  } = useSelectOptions();

  const userGender = JSON.parse(retrieveToken("user"));
  const gender = userGender?.gender;
  const filterData = getFilterData(gender);
  const selectOptions = {
    ...selectOptionsMap,
    age: getAgeOptions(gender),
    annual_income: mapToSelectOptions(incomeData),
    community:
      communityData?.map((item) => ({
        value: item.id,
        label: item.name,
      })) || [],
    nationality: nationality?.options || [],
    marital_status: maritalStatus?.options || [],
    family_status: familyStatus?.options,
    body_type: bodyType?.options,
    qualification: qualification?.options,
    profession: mapToSelectOptions(professionData),
    complexion: complexion?.options,
    diet: diet?.options,
    motherTongue: motherTongue?.options,
    education: mapToSelectOptions(courseData),
  };

  const dynamicSources = {
    annual_income: mapToSelectOptions(incomeData),
    community: communityData?.map((c) => ({ id: c.id, label: c.name })) || [],
    nationality: nationality?.options,
    marital_status: maritalStatus?.options,
    family_status: familyStatus?.options,
    family_type: familyType?.options,
    body_type: bodyType?.options,
    qualification: qualification?.options,
    profession: mapToSelectOptions(professionData),
    complexion: complexion?.options,
    diet: diet?.options,
    motherTongue: motherTongue?.options,
    education: mapToSelectOptions(courseData),
  };

  const dynamicFilterData = useMemo(() => {
    return filterData.map((item) => {
      const source = dynamicSources[item.id];

      if (source && source.length) {
        return {
          ...item,
          tags: source.map((c) => ({
            id: c.id ?? c.value,
            label: c.name ?? c.label,
          })),
        };
      }

      return item;
    });
  }, [filterData, dynamicSources]);

  const tabItems = [
    {
      key: "1",
      label: "Keyword",
      children: <KeywordTab open={open} setOpen={setOpen} />,
    },
    {
      key: "2",
      label: "Advanced",
      children: (
        <AdvancedTab
          savedSearches={savedSearches}
          isLoading={isLoading}
          setOpen={setOpen}
          dynamicFilterData={dynamicFilterData}
          selectOptions={selectOptions}
        />
      ),
    },
    {
      key: "3",
      label: "Saved",
      children: (
        <SavedTab
          savedSearches={savedSearches}
          isLoading={isLoading}
          selectOptions={dynamicFilterData}
          setOpen={setOpen}
        />
      ),
    },
  ];

  return (
    <div className="search-page">
      <h1 className="font-title-xl">Search profiles</h1>
      <p className="mt-1 description-text">
        Find your ideal match with advanced filters based on location,
        interests, and preferences. Feel free to type anything!
      </p>
      <Button
        onClick={() => setOpen(false)}
        variant="custom"
        height={32}
        className="close-icon"
      >
        <CloseIcon />
      </Button>
      <Tabs
        isCentered
        className="mt-6 search-tab"
        items={tabItems}
        defaultActiveKey="1"
        activeKey={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
}

export default SearchPage;
